import { parseCompositeIncidentId } from '@collector/shared-utils'
import { Incident } from '@collector/sportsapi-client'
import { IncidentId } from '@collector/sportsapi-client-legacy'

import { IncidentIdsGroup, IncidentsGroup } from './incidentsGroup'

type SubstitutionGroupingContext = {
  incidentIdsGroup: IncidentIdsGroup
  substitutionOutId: IncidentId
  substitutionInId: IncidentId
  incidentsById: Map<number, Incident>
  inByOutId: Map<number, Incident>
}

function createIncidentsByIdMap(incidents: Incident[]): Map<number, Incident> {
  return new Map(incidents.map((incident) => [incident.id, incident]))
}

function buildSubstitutionInByOutIdMap(
  incidents: Incident[],
  incidentsById: Map<number, Incident>,
  substitutionOutId: IncidentId,
  substitutionInId: IncidentId,
): Map<number, Incident> {
  const inByOutId = new Map<number, Incident>()

  for (const incident of incidents) {
    if (incident.incident_id !== substitutionInId) {
      continue
    }

    const parentOutId = parseCompositeIncidentId(incident.parent_id)

    if (parentOutId === null) {
      continue
    }

    const parentOut = incidentsById.get(parentOutId)

    if (parentOut?.incident_id === substitutionOutId) {
      inByOutId.set(parentOutId, incident)
    }
  }

  return inByOutId
}

function isSubstitutionIncident(
  incident: Incident,
  substitutionOutId: IncidentId,
  substitutionInId: IncidentId,
): boolean {
  return (
    incident.incident_id === substitutionOutId ||
    incident.incident_id === substitutionInId
  )
}

function isPairedSubstitutionIn(
  incident: Incident,
  context: SubstitutionGroupingContext,
): boolean {
  const parentOutId = parseCompositeIncidentId(incident.parent_id)
  const parentOut =
    parentOutId !== null ? context.incidentsById.get(parentOutId) : undefined

  return (
    parentOut?.incident_id === context.substitutionOutId &&
    context.inByOutId.get(parentOut.id) === incident
  )
}

function appendSubstitutionOutIncident(
  incident: Incident,
  result: (Incident | IncidentsGroup)[],
  consumedIds: Set<number>,
  context: SubstitutionGroupingContext,
): void {
  const inIncident = context.inByOutId.get(incident.id)

  if (inIncident) {
    consumedIds.add(incident.id)
    consumedIds.add(inIncident.id)
    result.push({
      name: context.incidentIdsGroup.name,
      incidents: [inIncident, incident],
    })

    return
  }

  result.push({
    name: context.incidentIdsGroup.name,
    incidents: [incident],
  })
}

function buildGroupedIncidents(
  incidents: Incident[],
  context: SubstitutionGroupingContext,
): (Incident | IncidentsGroup)[] {
  const consumedIds = new Set<number>()
  const result: (Incident | IncidentsGroup)[] = []

  for (const incident of incidents) {
    if (consumedIds.has(incident.id)) {
      continue
    }

    if (
      !isSubstitutionIncident(
        incident,
        context.substitutionOutId,
        context.substitutionInId,
      )
    ) {
      result.push(incident)
      continue
    }

    if (incident.incident_id === context.substitutionOutId) {
      appendSubstitutionOutIncident(incident, result, consumedIds, context)
      continue
    }

    if (isPairedSubstitutionIn(incident, context)) {
      continue
    }

    result.push({
      name: context.incidentIdsGroup.name,
      incidents: [incident],
    })
  }

  return result
}

export function buildIncidentsGroups(
  incidents: Incident[],
  incidentIdsGroup: IncidentIdsGroup | null,
): (Incident | IncidentsGroup)[] {
  if (!incidentIdsGroup) {
    return [...incidents]
  }

  const substitutionOutId = incidentIdsGroup.incidentIds[0]
  const substitutionInId = incidentIdsGroup.incidentIds[1]
  const incidentsById = createIncidentsByIdMap(incidents)
  const inByOutId = buildSubstitutionInByOutIdMap(
    incidents,
    incidentsById,
    substitutionOutId,
    substitutionInId,
  )

  return buildGroupedIncidents(incidents, {
    incidentIdsGroup,
    substitutionOutId,
    substitutionInId,
    incidentsById,
    inByOutId,
  })
}
